<template>
  <div
    class="ds-tabs"
    role="tablist"
  >
    <button
      v-for="(tab, index) in tabs"
      :id="tabId(tab.key)"
      :key="tab.key"
      ref="tab"
      type="button"
      role="tab"
      class="ds-tab"
      :class="{ active: tab.key === value }"
      :aria-selected="tab.key === value ? 'true' : 'false'"
      :aria-controls="panelId(tab.key)"
      :tabindex="tab.key === value ? 0 : -1"
      @click="$emit('input', tab.key)"
      @keydown.left.prevent="move(index, -1)"
      @keydown.right.prevent="move(index, 1)"
      @keydown.home.prevent="move(0, 0)"
      @keydown.end.prevent="move(tabs.length - 1, 0)"
    >
      {{ tab.label }}
      <span
        v-if="tab.count != null"
        class="ds-tab-count"
      >{{ tab.count }}</span>
    </button>
  </div>
</template>

<script>
// Segmented control. v-model holds the active key; the parent renders the panel, so the
// parent decides whether panels are lazy (v-if, like b-tab's `lazy`) or kept alive (v-show,
// for panels holding unsaved edits).
//
// The parent's panel must carry role="tabpanel" and :id="panelId" so aria-controls
// resolves. DsTabs exposes both id helpers as scoped-slot-free public methods.
export default {
  name: 'DsTabs',
  props: {
    // [{ key, label, count? }]
    tabs: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
    // Namespaces the generated ids when more than one tablist is on a page.
    idPrefix: {
      type: String,
      default: 'ds-tabs',
    },
  },
  methods: {
    tabId(key) {
      return `${this.idPrefix}-tab-${key}`
    },
    panelId(key) {
      return `${this.idPrefix}-panel-${key}`
    },
    // Arrow / Home / End move between tabs, per the ARIA tabs pattern.
    move(index, delta) {
      const next = (index + delta + this.tabs.length) % this.tabs.length
      const tab = this.tabs[next]
      if (!tab) return
      this.$emit('input', tab.key)
      this.$nextTick(() => {
        const buttons = this.$refs.tab || []
        if (buttons[next]) buttons[next].focus()
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.ds-tabs {
  display: inline-flex;
  gap: 3px;
  padding: 4px;
  background: $c-white;
  border: 1px solid $c-border;
  border-radius: 12px;
}

.ds-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 15px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: $c-text-sec;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not(.active) {
    background: $c-bg;
    color: $c-text;
  }

  &.active {
    background: $c-wine;
    color: $c-white;
  }

  &:focus-visible {
    @include ds-focus-ring;
  }
}

.ds-tab-count {
  padding: 1px 7px;
  border-radius: 100px;
  background: $c-bg;
  color: $c-text-sec;
  font-size: 11px;
  font-weight: 700;

  .ds-tab.active & {
    background: rgba($c-white, 0.18);
    color: $c-white;
  }
}
</style>
